import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { MagicWand, Trash, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TimePickerPopover } from '@/components/ui/time-picker-popover';
import { TeamPicker } from '@/components/schedule/TeamPicker';
import { listLocations } from '@/api/locations';
import { SLOT_KINDS, type SlotInput, type SlotKind } from '@/api/templates';
import type { TeamLite } from '@/hooks/useAsyncTeamSearch';

// TemplateSlotFormModal — create/edit form for a template slot. Mirrors
// ActivityFormModal but uses (day_of_week, HH:MM) instead of full
// datetime since template slots have no specific date.
//
// The modal renders as a div-based overlay (not a native <dialog>) so it
// composes cleanly with the schedule's other layers — same pattern as
// LocationFormModal after we standardised away from native dialogs.

interface Props {
  slug: string;
  // Pre-fill values for both create and edit.
  initial?: Partial<SlotInput> & { id?: string };
  onSubmit: (input: SlotInput) => Promise<unknown>;
  onCancel: () => void;
  // Edit-mode delete. Parent owns the confirm + mutate flow.
  onDelete?: () => void;
  submitting: boolean;
  deleting?: boolean;
  error: string | null;
}

const DAY_LABELS_KEYS = [
  'common.day.mon',
  'common.day.tue',
  'common.day.wed',
  'common.day.thu',
  'common.day.fri',
  'common.day.sat',
  'common.day.sun',
] as const;

export function TemplateSlotFormModal({
  slug,
  initial,
  onSubmit,
  onCancel,
  onDelete,
  submitting,
  deleting,
  error,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const modalRef = useRef<HTMLDivElement>(null);
  const isEdit = !!initial?.id;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [kind, setKind] = useState<SlotKind>(initial?.kind ?? 'training');
  const [dayOfWeek, setDayOfWeek] = useState<number>(initial?.day_of_week ?? 1);
  // Normalise incoming times — the backend may hand us "HH:MM:SS" or
  // "HH:MM:SS.ffffff" from Postgres TIME, but the picker + the submit
  // contract use "HH:MM". Strip seconds at the boundary.
  const [startTime, setStartTime] = useState(normaliseHHMM(initial?.start_time ?? '17:00'));
  const [endTime, setEndTime] = useState(normaliseHHMM(initial?.end_time ?? '18:00'));
  const [locationId, setLocationId] = useState(initial?.location_id ?? '');
  const [zoneId, setZoneId] = useState(initial?.zone_id ?? '');
  // Picker emits the full {id, name, color}; we seed with a thin
  // {id, name: '', color: ''} when only the id is known from `initial`
  // and let the picker resolve the label.
  const [team, setTeam] = useState<TeamLite | null>(
    initial?.team_id ? { id: initial.team_id, name: '', color: '' } : null,
  );
  const teamId = team?.id ?? '';
  const selectedTeam = team && team.name ? team : null;

  const { data: locationsRes } = useQuery({
    queryKey: ['locations', slug, ''],
    queryFn: () => listLocations(token, slug),
  });
  const locationsList = locationsRes?.locations ?? [];
  const selectedLocation = locationsList.find((l) => l.id === locationId);
  const zonesForLocation = useMemo(
    () => selectedLocation?.zones ?? [],
    [selectedLocation],
  );

  // Reset zone when its parent location changes. Gated on
  // `selectedLocation` being defined — otherwise this effect would fire
  // on mount (before the locations query resolves), see an empty
  // zonesForLocation, and wipe any zoneId we were just seeded with from
  // `initial`. That bug silently stripped zone_id from every slot edit
  // until now.
  useEffect(() => {
    if (
      locationId &&
      selectedLocation &&
      !zonesForLocation.some((z) => z.id === zoneId)
    ) {
      setZoneId('');
    }
  }, [locationId, zoneId, zonesForLocation, selectedLocation]);

  // Escape-to-cancel + window-capture pointerdown so any overlapping
  // outside-click watchers (radix etc.) ignore clicks inside this modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      const el = modalRef.current;
      if (el && el.contains(e.target as Node)) {
        e.stopPropagation();
      }
    }
    window.addEventListener('pointerdown', onPointerDown, { capture: true });
    return () => window.removeEventListener('pointerdown', onPointerDown, { capture: true });
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    if (!teamId) return;
    if (startTime >= endTime) return;
    await onSubmit({
      title: title.trim(),
      kind,
      day_of_week: dayOfWeek,
      start_time: startTime,
      end_time: endTime,
      ...(locationId ? { location_id: locationId } : { location_id: null }),
      ...(zoneId ? { zone_id: zoneId } : { zone_id: null }),
      team_id: teamId,
    });
  }

  // generateTitle composes a sensible default from the slot's kind + the
  // selected team. "Match A-lag" / "Träning A-lag" — kind "other" is
  // dropped since it adds no information.
  function generateTitle() {
    if (!selectedTeam) return;
    const prefix = kind === 'other' ? '' : t(`templates.slot.kindOption.${kind}`);
    const composed = [prefix, selectedTeam.name].filter(Boolean).join(' ').trim();
    if (composed) setTitle(composed);
  }

  const titleError = !title.trim() ? t('schedule.validation.titleRequired') : null;
  const teamError = !teamId ? t('schedule.validation.teamRequired') : null;
  const timeError = startTime >= endTime ? t('schedule.validation.timeOrdering') : null;

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/30 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[90vh] w-[min(92vw,40rem)] flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="font-heading text-lg font-bold">
            {isEdit ? t('templates.slot.editTitle') : t('templates.slot.createTitle')}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('common.cancel')}
            onClick={onCancel}
          >
            <X weight="bold" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <label className="block">
            <span className="font-heading text-sm font-semibold">
              {t('templates.slot.title')}
            </span>
            <div className="mt-1 flex items-stretch gap-2">
              <Input
                required
                autoFocus
                className="flex-1"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={generateTitle}
                aria-label={t('templates.slot.autoTitle')}
                title={t('templates.slot.autoTitle')}
                disabled={!selectedTeam}
              >
                <MagicWand weight="bold" />
              </Button>
            </div>
          </label>

          <label className="mt-4 block">
            <span className="font-heading text-sm font-semibold">
              {t('templates.slot.kind')}
            </span>
            <div className="mt-2 flex flex-wrap gap-2">
              {SLOT_KINDS.map((k) => {
                const active = kind === k;
                return (
                  <label
                    key={k}
                    className={
                      'cursor-pointer rounded-pill border px-3 py-1 text-xs font-semibold transition-colors ' +
                      (active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground/80 hover:bg-muted')
                    }
                  >
                    <input
                      type="radio"
                      name="slot-kind"
                      value={k}
                      checked={active}
                      onChange={() => setKind(k)}
                      className="sr-only"
                    />
                    {t(`templates.slot.kindOption.${k}`)}
                  </label>
                );
              })}
            </div>
          </label>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-heading text-sm font-semibold">
                {t('templates.slot.dayOfWeek')}
              </span>
              <Select
                value={String(dayOfWeek)}
                onValueChange={(v) => setDayOfWeek(parseInt(v, 10))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent container={modalRef.current}>
                  {DAY_LABELS_KEYS.map((key, idx) => (
                    <SelectItem key={key} value={String(idx + 1)}>
                      {t(key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <div className="block">
              <span className="font-heading text-sm font-semibold">
                {t('templates.slot.startTime')}
              </span>
              <TimePickerPopover
                className="mt-1"
                value={startTime}
                onChange={setStartTime}
                ariaLabel={t('templates.slot.startTime')}
              />
            </div>

            <div className="block">
              <span className="font-heading text-sm font-semibold">
                {t('templates.slot.endTime')}
              </span>
              <TimePickerPopover
                className="mt-1"
                value={endTime}
                onChange={setEndTime}
                ariaLabel={t('templates.slot.endTime')}
              />
            </div>
          </div>
          {timeError && <p className="mt-1 text-xs text-destructive">{timeError}</p>}

          <div className="mt-4">
            <span className="font-heading text-sm font-semibold">
              {t('templates.slot.team')}
            </span>
            <TeamPicker
              slug={slug}
              value={team}
              onChange={setTeam}
              placeholder={t('templates.slot.teamPlaceholder')}
              searchPlaceholder={t('templates.slot.teamSearchPlaceholder')}
              emptyLabel={t('templates.slot.teamEmpty')}
            />
            {teamError && <p className="mt-1 text-xs text-destructive">{teamError}</p>}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-heading text-sm font-semibold">
                {t('templates.slot.location')}
              </span>
              <Select
                value={locationId === '' ? '__none__' : locationId}
                onValueChange={(v) => setLocationId(v === '__none__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent container={modalRef.current}>
                  <SelectItem value="__none__">{t('schedule.activity.noLocation')}</SelectItem>
                  {locationsList.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="font-heading text-sm font-semibold">
                {t('templates.slot.zone')}
              </span>
              <Select
                value={zoneId === '' ? '__none__' : zoneId}
                onValueChange={(v) => setZoneId(v === '__none__' ? '' : v)}
                disabled={!locationId || zonesForLocation.length === 0}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent container={modalRef.current}>
                  <SelectItem value="__none__">{t('schedule.activity.noZone')}</SelectItem>
                  {zonesForLocation.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          {error && (
            <p
              role="alert"
              className="mt-4 rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          {titleError && (
            <p className="mt-2 text-xs text-destructive">{titleError}</p>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-border px-6 py-4">
          {isEdit && onDelete ? (
            <Button
              type="button"
              variant="outline"
              onClick={onDelete}
              disabled={submitting || deleting}
              className="text-destructive"
            >
              <Trash weight="bold" />
              {deleting ? t('locations.form.saving') : t('templates.slot.delete')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !!titleError || !!teamError || !!timeError}
            >
              {submitting ? t('locations.form.saving') : t('locations.form.save')}
            </Button>
          </div>
        </footer>
      </form>
    </div>
  );
}

// normaliseHHMM accepts "HH:MM", "HH:MM:SS" or "HH:MM:SS.ffffff" and
// returns "HH:MM". Postgres TIME values come back from the API with
// seconds appended; the time picker + the submit contract use HH:MM
// only.
function normaliseHHMM(s: string | undefined | null): string {
  if (!s) return '';
  const m = s.match(/^(\d{2}:\d{2})/);
  return m && m[1] ? m[1] : s;
}
